import { equipmentState } from './state';
function isUltravioletItem(item) {
    const value = typeof item === 'string' ? item : item?.name || item?.description || '';
    if (!value) return false;
    return /ультрафиолет|уф\b|ultraviolet|uv\b/i.test(value);
}

function isHeatPumpItem(source) {
    const value = typeof source === 'string'
        ? source
        : source?.name || source?.description || '';
    if (!value) return false;
    if (/теплообменник/i.test(value)) return false;
    return /теплов(ой|ые)\s*насос|heat\s*pump|heatpump/i.test(value);
}


/**
 * Вспомогательная функция для получения источника данных: аргумент -> кэш -> пустой массив
 */
function getSource(data) {
    if (Array.isArray(data)) return data;
    return equipmentState.getData() || [];
}

export function getEquipmentByCategory(category, source) {
    return getSource(source).filter(item => item.category === category);
}

export function getPumps(source) {
    return getEquipmentByCategory('pump', source);
}

export function getFilters(source) {
    return getEquipmentByCategory('filters', source);
}

export function getHeatExchangers(source) {
    return getEquipmentByCategory('heaters', source).filter(item =>
        /теплообмен|heat.exchanger|[HН][EЕ]\s*[-]?\d+/i.test(item.name)
    );
}

export function getElectricHeaters(source) {
    return getEquipmentByCategory('heaters', source).filter(item =>
        /электронагреватель|электрический|electric/i.test(item.name)
    );
}

export function getHeatPumps(source) {
    return getSource(source).filter(isHeatPumpItem);
}

export function getSkimmers(source) {
    return getSource(source).filter(item => {
        const name = item.name.toLowerCase();
        if (name.includes('130sv') || name.includes('aquaviva 130sv')) return false;
        return /скиммер|skimmer/i.test(item.name);
    });
}

export function getNozzles(source) {
    return getSource(source).filter(item => /форсунк|nozzle/i.test(item.name));
}

export function getDrains(source) {
    return getSource(source).filter(item => {
        const name = (item.name || '').toLowerCase();
        return /слив|дренаж|drain/i.test(item.name) || name.includes('трап');
    });
}

export function getGrates(source) {
    return getSource(source).filter(item =>
        /решетк|grate|переливн/i.test(item.name) && !/профиль|уголок|profile|corner/i.test(item.name)
    );
}

export function getGrateProfile(source) {
    return getSource(source).filter(item =>
        /профиль.*переливн|profile.*(grate|overflow)/i.test(item.name)
    );
}

export function getGrateCorners(source) {
    return getSource(source).filter(item =>
        /угол.*переливн|corner.*(grate|overflow)/i.test(item.name)
    );
}

/**
 * Автодолив — авто-добавляемая позиция скиммера/переливной решётки (не
 * выбирается руками). Регекспы синхронизированы с сервером
 * (autofill/config.js AUTOFILL_SEARCH_PATTERNS) — если поменяются там,
 * поменять и здесь.
 */
export function getAutofill(source) {
    return getSource(source).filter((item) => {
        const text = `${item?.name || ''} ${item?.description || ''}`.toLowerCase();
        return (
            /автодолив/.test(text) ||
            /автоматич[а-яё]*\s+долив/.test(text) ||
            /долив[а-яё]*\s+вод/.test(text) ||
            /auto\s*fill/.test(text) ||
            /water\s*level/.test(text)
        );
    });
}

/**
 * Каталог для API расчёта скиммеров: скиммеры + автодолив (без дублей по id).
 * Нужен отдельно от getSkimmers — сервер ищет кандидата автодолива ВНУТРИ
 * переданного каталога (getRecommendedAutoFill), поэтому чистый список
 * скиммеров его никогда не найдёт (тот же принцип, что и у решёток, см.
 * getGrateCalculationCatalog).
 * @param {Array|undefined} source
 * @returns {Array}
 */
export function getSkimmerCalculationCatalog(source) {
    const seen = new Set();
    const out = [];
    const push = (item) => {
        if (!item) return;
        const key = item.id != null ? String(item.id) : String(item.name || '');
        if (!key || seen.has(key)) return;
        seen.add(key);
        out.push(item);
    };
    getSkimmers(source).forEach(push);
    getAutofill(source).forEach(push);
    return out;
}

/** Сливная воронка переливного лотка (из YML или fallback на сервере). */
export function getGrateOverflowFunnel(source) {
    return getSource(source).filter((item) => {
        const text = `${item?.name || ''} ${item?.description || ''}`.toLowerCase();
        return (
            (text.includes('воронк') && text.includes('слив')) ||
            text.includes('сливная воронка') ||
            (text.includes('воронк') && text.includes('перелив'))
        );
    });
}

/**
 * Каталог для API расчёта решёток: решётки + профиль + воронка + автодолив
 * (без дублей по id). Автодолив нужен здесь по той же причине, что и в
 * getSkimmerCalculationCatalog — getRecommendedAutoFill ищет кандидата
 * ВНУТРИ этого каталога.
 * @param {Array|undefined} source
 * @returns {Array}
 */
export function getGrateCalculationCatalog(source) {
    const seen = new Set();
    const out = [];
    const push = (item) => {
        if (!item) return;
        const key = item.id != null ? String(item.id) : String(item.name || '');
        if (!key || seen.has(key)) return;
        seen.add(key);
        out.push(item);
    };
    getGrates(source).forEach(push);
    getGrateProfile(source).forEach(push);
    getGrateOverflowFunnel(source).forEach(push);
    getAutofill(source).forEach(push);
    return out;
}

export function getChlorinators(source) {
    return getSource(source).filter(item => /хлоринатор|chlorinator|электролиз/i.test(item?.name || ''));
}

export function getUltraviolet(source) {
    return getSource(source).filter(isUltravioletItem);
}

export function getPVC(source) {
    return getSource(source).filter(item => {
        const name = item.name.toLowerCase();
        const blacklist = ['уголок', 'герметик', 'модульное', 'полоса', 'скиммер', 'солярная', 'слив', 'фен', 'step', 'форсунка'];
        if (blacklist.some(exc => name.includes(exc))) return false;
        return name.includes('пвх') || name.includes('пленк') || name.includes('pvc');
    });
}

export function getMosaic(source) {
    return getSource(source).filter(item => {
        const name = item.name.toLowerCase();
        const blacklist = ['пвх', 'алькорплан', 'пленка', 'pvc', 'film', 'уголок'];
        if (blacklist.some(exc => name.includes(exc))) return false;
        return name.includes('мозаик') || name.includes('mosaic');
    });
}

export function getProjectors(source) {
    return getSource(source).filter(item => {
        const name = item.name.toLowerCase();
        const blacklist = ['закладная', 'лампа', 'робот', 'мозаика', 'фонтан', 'алькорплан', 'пульт', 'водопад', 'герметик', 'лента', 'par56'];
        if (blacklist.some(exc => name.includes(exc))) return false;
        return /прожектор|свет|light|led|светильник|подсветка/i.test(item.name);
    });
}

export function getTransformers(source) {
    return getSource(source).filter(item => /cb.*трансформатор|трансформатор.*cb/i.test(item.name));
}

export function getLadders(source) {
    return getSource(source).filter(item => /лестниц|ladder/i.test(item.name));
}

export function getSand(source) {
    return getEquipmentByCategory('sand', source);
}

export function getConsoles(source) {
    return getEquipmentByCategory('console', source).filter(i => /пульт|консоль|console/i.test(i.name));
}

export function getProjectorEmbedded(source) {
    return getSource(source).filter(item =>
        /закладная.*прожектор|закладная.*led|прожектор.*закладная|led.*закладная/i.test(item.name)
    );
}

export function getMonoblocks(source) {
    return getSource(source).filter(isValidMonoblockItem);
}

export function filterEquipmentByCategory(equipment, category) {
    return (Array.isArray(equipment) ? equipment : []).filter((item) => item.category === category);
}

export function getPumpOnly(equipment) {
    return filterEquipmentByCategory(equipment, 'pump');
}

export function getHeatExchangersOnly(equipment) {
    return filterEquipmentByCategory(equipment, 'heaters').filter(item =>
        /теплообмен|heat.exchanger/i.test(item.name)
    );
}

export function getElectricHeatersOnly(equipment) {
    return filterEquipmentByCategory(equipment, 'heaters').filter(item =>
        /электронагреватель|электрический|electric/i.test(item.name)
    );
}

const MONOBLOCK_PATTERNS = {
    FLOW_EXTRACT: /(?:производительность\s*)?(\d+(?:[.,]\d+)?)\s*(?:м3\/ч|м³\/ч|куб\.м\/ч)/i,
    INCLUDE: ['моноблок', 'фильтровальная установка', 'комплект фильтрации'],
    EXCLUDE: ['только фильтр', 'корпус фильтра'],
    SAND_LOAD_KG: [
        /загрузка\s*песка[:\s]*(\d+)/i,
        /(\d+)\s*кг\s*песка/i,
        /песок\s*(\d+)\s*кг/i
    ]
};

export function isValidMonoblockItem(item) {
    if (!item || !item.name) return false;
    const name = item.name.toLowerCase();

    if (MONOBLOCK_PATTERNS.EXCLUDE.some(bad => name.includes(bad))) return false;
    return MONOBLOCK_PATTERNS.INCLUDE.some(kw => name.includes(kw));
}
